import { Component, HostBinding, Input, OnChanges, SimpleChange, SimpleChanges } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

/** Prefix for the path to i18n keys for this component */
const I18N_PREFIX = 'GALAXY.UPLOADER.CONSTRAINTS';

/** Quick access to keys specifically used by this component */
const I18NKeys = {
  image: `${I18N_PREFIX}.IMAGE_TYPE`,
  images: `${I18N_PREFIX}.IMAGE_TYPE_PLURAL`,
  video: `${I18N_PREFIX}.VIDEO_TYPE`,
  audio: `${I18N_PREFIX}.AUDIO_TYPE`,
  and: `${I18N_PREFIX}.AND`,
};

/**
 * Quick access to file group specifiers.
 * Can expand if we want to accept more types, IE) application/*
 */
const FileGroupSpecifier = {
  image: 'image/*',
  video: 'video/*',
  audio: 'audio/*',
};

@Component({
  selector: 'glxy-uploader-constraints-text',
  templateUrl: './uploader-constraints-text.component.html',
  styleUrls: ['./uploader-constraints-text.component.scss'],
  standalone: false,
})
export class GalaxyUploaderConstraintsTextComponent implements OnChanges {
  @HostBinding('class') class = 'glxy-uploader-constraints-text';

  /** Max number of files that can be uploaded */
  @Input() maxFiles?: number;

  /** Max size of files that can be uploaded */
  @Input() maxFileSize?: number;

  /** Accept list for file types that can be uploaded */
  @Input() accept?: string;

  /** If provided, will be used instead of generated text */
  @Input() text?: string;

  acceptDisplay?: string;
  fileSizeDisplay?: string;

  constructor(private translate: TranslateService) {}

  ngOnChanges(changes: SimpleChanges): void {
    const { accept = {} as SimpleChange, maxFileSize = {} as SimpleChange, text = {} as SimpleChange } = changes;

    // Bail out early to avoid unecessary text generation
    if (text.currentValue !== undefined && text.currentValue !== null) {
      return;
    }

    if (accept.currentValue !== accept.previousValue) {
      this.acceptDisplay = this.convertAcceptToDisplay(this.accept || '');
    }

    if (maxFileSize.currentValue !== maxFileSize.previousValue) {
      this.fileSizeDisplay = this.convertSizeToDisplay(this.maxFileSize || 0);
    }
  }

  /**
   * Given the accept list, break it down into a series of display values
   *
   * @param accept - accept list provided to the component
   */
  private convertAcceptToDisplay(accept: string): string {
    const list = accept
      .trim()
      .split(',')
      .map((spec: string) => this.specifierToDisplay(spec));

    // Uppercase the first character if sentence doesn't start with max files
    if (!this.maxFiles) {
      const first = list[0].split('');
      first[0] = first[0].toUpperCase();
      list[0] = first.join('');
    }

    let joinChar = '';
    if (list.length > 2) {
      // Comma separated for three or more
      // PDF, JPEG, Video
      joinChar = ', ';
    } else if (list.length === 2) {
      // And separated for two
      // PDF and JPEG
      joinChar = this.translate.instant(I18NKeys.and);
      joinChar = ` ${joinChar} `;
    } else {
      // For images, remove 'files' off the end
      // "Up to X images"
      if (list[0] === this.translate.instant(I18NKeys.image)) {
        if (this.maxFiles && this.maxFiles > 1) {
          // pluralize
          list[0] = this.translate.instant(I18NKeys.images);
        }
        return list.join();
      }
    }

    return list.join(joinChar) + ' ' + this.translate.instant('GALAXY.UPLOADER.CONSTRAINTS.FILES');
  }

  /**
   * Convert a uniique file specifier into the right display format.
   * IE) image/* -> images, .pdf -> PDF
   *
   * @param fileType - The file type specifier to convert
   */
  private specifierToDisplay(fileType: string): string {
    switch (fileType) {
      case FileGroupSpecifier.audio:
        return this.translate.instant(I18NKeys.audio);
      case FileGroupSpecifier.image:
        return this.translate.instant(I18NKeys.image);
      case FileGroupSpecifier.video:
        return this.translate.instant(I18NKeys.video);
      default: {
        // If file extension, uppercase it and remove period
        const fileSplit = fileType.split('.');
        return (fileSplit[1] || fileSplit[0]).toUpperCase();
      }
    }
  }

  /**
   * Convert the max file size constraint to a display value
   * IE) 12 B, 7 KB, 100 MB
   *
   * @param maxSize - Max files size allowed for upload
   */
  private convertSizeToDisplay(maxSize: number): string {
    if (maxSize < 1024) {
      return maxSize + ' B';
    } else if (maxSize >= 1024 && maxSize < 1048576) {
      return (maxSize / 1024).toFixed(1) + ' KB';
    }
    return (maxSize / 1048576).toFixed(1) + ' MB';
  }
}
